"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  GalleryDevice,
  GalleryImageData,
} from "../../blocks/image-gallery/image-gallery.types";
import { Button } from "../../elements/button/button";
import { Image } from "../../elements/image/image";
import { IconArrowLeft, IconArrowRight, IconVideoPlay } from "../../icons";
import { cn } from "../../lib/utils";

export interface CarouselThumbnailsProps {
  /** Array of gallery images */
  images: GalleryImageData[];

  /** Currently active thumbnail index */
  activeIndex: number;

  /** Callback when thumbnail is clicked */
  onThumbnailClick: (index: number) => void;

  /** Images count variant matching Figma */
  imagesVariant?: "5 images" | "More than 5";

  /** Scroll position from Figma variant */
  scrolled?: "Default" | 2 | 4 | 6 | 8 | 10;

  /** Width variant from Figma */
  width?: "Default" | "Full";

  /** Device type from Figma */
  device?: GalleryDevice;

  /** Product type from Figma */
  product?: "Default" | "Shoes";

  /** Microcopies for navigation */
  microcopies: {
    previous: string;
    next: string;
  };

  /** Show navigation arrows (when > 5 images) */
  showNavigationArrows?: boolean;

  /** Additional CSS classes */
  className?: string;
}

const MAX_DEFAULT_THUMBNAILS = 5;
const DEFAULT_THUMB_WIDTH = 64;
const THUMBNAIL_GAP = 8;
const SCROLL_ITEMS_COUNT = 2;

export function CarouselThumbnails(props: CarouselThumbnailsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const isMobile = props.device === "Mobile";
  // Full width only applies on desktop (CSS handles actual responsive behavior)
  const isFullWidth = props.width === "Full" && !isMobile;

  // Show arrows:
  // - Full width (overlay on desktop): only if content overflows
  // - Default: if > 5 images and not mobile
  // - Never on mobile (mobile uses swipe/scroll)
  const showArrows = isFullWidth
    ? hasOverflow
    : (props.showNavigationArrows ??
        props.images.length > MAX_DEFAULT_THUMBNAILS) &&
      !isMobile;

  const checkScrollability = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const isOverflowing = container.scrollWidth > container.clientWidth;
      setHasOverflow(isOverflowing);
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  }, []);

  useEffect(() => {
    checkScrollability();
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollability);
      window.addEventListener("resize", checkScrollability);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollability);
      }
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollability]);

  useEffect(() => {
    const container = containerRef.current;
    const activeThumbnail = container?.children.item(
      props.activeIndex
    ) as HTMLElement | null;

    if (!(container && activeThumbnail)) {
      return;
    }

    // Keep auto-centering scoped to the horizontal thumbnail strip.
    const left =
      activeThumbnail.offsetLeft -
      (container.clientWidth - activeThumbnail.offsetWidth) / 2;

    if (typeof container.scrollTo === "function") {
      container.scrollTo({
        left,
        behavior: "smooth",
      });
    } else {
      container.scrollLeft = left;
    }
  }, [props.activeIndex]);

  const scrollBy = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (container) {
      // Get actual thumbnail width from DOM + gap
      const firstThumb = container.querySelector("button");
      const thumbWidth = firstThumb
        ? firstThumb.offsetWidth
        : DEFAULT_THUMB_WIDTH;
      const itemSize = thumbWidth + THUMBNAIL_GAP;
      const scrollAmount = itemSize * SCROLL_ITEMS_COUNT;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      className={cn(
        "relative flex w-full items-center justify-center gap-2",
        props.className
      )}
    >
      {/* Left Arrow - Show when can scroll (hidden on mobile for overlay) */}
      {showArrows && (
        <Button
          aria-label={props.microcopies.previous}
          className={cn(
            "h-8 w-8 flex-shrink-0 transition-opacity",
            canScrollLeft ? "opacity-100" : "pointer-events-none opacity-30"
          )}
          disabled={!canScrollLeft}
          onClick={() => scrollBy("left")}
          size="icon"
          variant="outline"
        >
          <IconArrowLeft className="!h-4 !w-4" />
        </Button>
      )}

      {/* Thumbnails Container
          - Mobile: Always fixed width for 5 thumbnails (16.0625rem / 257px)
          - Desktop with Full width: Flexible width, shows all that fit
          - Desktop default: Fixed width for 5 thumbnails (22rem / 352px) */}
      <div
        className={cn(
          "scrollbar-hide flex items-center gap-2 overflow-x-auto",
          hasOverflow ? "justify-start" : "justify-center",
          props.width === "Full"
            ? "w-[16.0625rem] md:w-[22rem] lg:w-auto lg:max-w-full"
            : "w-[16.0625rem] md:w-[22rem]"
        )}
        ref={containerRef}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {props.images.map((image, index) => {
          const isActive = index === props.activeIndex;
          const imageKey = image.src || `thumbnail-${String(index)}`;

          return (
            <button
              aria-current={isActive ? "true" : undefined}
              aria-label={`Thumbnail ${index + 1}`}
              className={cn(
                "relative h-[2.8125rem] w-[2.8125rem] flex-shrink-0 overflow-hidden transition-all md:h-16 md:w-16",
                "rounded bg-white", // borderRadius: 4px
                isActive
                  ? "border border-[#71717A] opacity-100" // Active: strokeWeight: 1px, full opacity
                  : "border-0 opacity-60 hover:opacity-100" // Inactive: 60% opacity per Figma
              )}
              key={imageKey}
              onClick={() => props.onThumbnailClick(index)}
              type="button"
            >
              <Image
                alt={image.alt}
                aspectRatio="1:1"
                className="h-full w-full object-cover"
                sizes="(min-width: 768px) 64px, 45px"
                src={image.thumbnailSrc || image.src}
              />
              {image.mediaType === "video" && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <IconVideoPlay
                    className="h-4 w-4 text-white"
                    fill="white"
                    stroke="white"
                    strokeWidth="1"
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Right Arrow - Show when can scroll (hidden on mobile for overlay) */}
      {showArrows && (
        <Button
          aria-label={props.microcopies.next}
          className={cn(
            "h-8 w-8 flex-shrink-0 transition-opacity",
            canScrollRight ? "opacity-100" : "pointer-events-none opacity-30"
          )}
          disabled={!canScrollRight}
          onClick={() => scrollBy("right")}
          size="icon"
          variant="outline"
        >
          <IconArrowRight className="!h-4 !w-4" />
        </Button>
      )}
    </div>
  );
}
